import { useEffect, useMemo, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useQueryList } from '@percent/utility'

const ORG_SEARCH_DEBOUNCE_MS = 300
const MIN_DEBOUNCED_QUERY_LENGTH = 3

export function useOrganisationLookup(countryCode: string) {
  const { nonprofitsService } = useServices()
  const [orgSearchQuery, setOrgSearchQuery] = useState('')
  const [lastSearchedCountry, setLastSearchedCountry] = useState('')

  const [debouncedOrgQuery] = useDebounceValue(orgSearchQuery, ORG_SEARCH_DEBOUNCE_MS)

  const shouldSearch = !!countryCode && debouncedOrgQuery.length >= MIN_DEBOUNCED_QUERY_LENGTH

  const [{ data: orgSearchData, isLoading: isOrgSearchLoading }] = useQueryList(
    ['getOrganisations', debouncedOrgQuery, countryCode],
    nonprofitsService.getOrganisations,
    { query: debouncedOrgQuery, pageSize: 10, countryCode, type: ['nonprofit'] },
    { enabled: shouldSearch, placeholderData: undefined }
  )

  useEffect(() => {
    setLastSearchedCountry('')
  }, [countryCode])

  useEffect(() => {
    if (shouldSearch) {
      setLastSearchedCountry(countryCode)
    }
  }, [shouldSearch, countryCode])

  // Results arrive for whichever country was searched, so consumers building their own options
  // need this to avoid showing results from the country the user just switched away from.
  const isSearchDataForSelectedCountry = countryCode === lastSearchedCountry

  const orgOptions = useMemo(
    () =>
      isSearchDataForSelectedCountry ? (orgSearchData ?? []).map(org => ({ value: org.id, label: org.name })) : [],
    [orgSearchData, isSearchDataForSelectedCountry]
  )

  return {
    orgSearchQuery,
    setOrgSearchQuery,
    orgSearchData,
    orgOptions,
    isSearchDataForSelectedCountry,
    isOrgSearchLoading
  }
}
